import React, { useState } from "react";
import EngineHeader from "../Equip2/EngineHeader";
import { Card, CardBody, Col, Container, Row } from "reactstrap";

const sidebarItems = [
  { icon: "⊞", label: "Overview" },
  { icon: "⚙", label: "Main Engine" },
  { icon: "⚙", label: "Aux. Engines" },
  { icon: "📈", label: "Performance" },
  { icon: "🛢", label: "Cargo & Tanks", active: true },
  { icon: "🔔", label: "Alerts", badge: 12 },
  { icon: "🔧", label: "Maintenance" },
  { icon: "📄", label: "Reports" },
  { icon: "💡", label: "AI Insights" },
  { icon: "⚙", label: "Settings" },
];

const quickActions = [
  { icon: "📋", label: "Cargo Log" },
  { icon: "📐", label: "Tank Calibration" },
  { icon: "📤", label: "Data Export" },
  { icon: "⚓", label: "Vessel Stability" },
  { icon: "📊", label: "Live Trend" },
];

const tanks = [
  { id: "1P", type: "Crude Oil", volume: 4120, capacity: 4500, level: 91, temp: 33.2, pressure: 2.70, density: 850.2, ullage: 1.32, status: "Normal", color: "#16a34a" },
  { id: "2P", type: "Crude Oil", volume: 3280, capacity: 4500, level: 72, temp: 31.5, pressure: 2.40, density: 848.7, ullage: 2.15, status: "Normal", color: "#2563eb" },
  { id: "3P", type: "Crude Oil", volume: 4920, capacity: 5500, level: 45, temp: 30.8, pressure: 2.30, density: 849.1, ullage: 3.40, status: "Normal", color: "#d97706" },
  { id: "4P", type: "Crude Oil", volume: 2040, capacity: 5500, level: 18, temp: 29.6, pressure: 2.10, density: 847.9, ullage: 5.02, status: "Normal", color: "#dc2626" },
  { id: "5S", type: "Slop/Residue", volume: 0, capacity: 5300, level: 0, temp: 28.9, pressure: 1.80, density: null, ullage: 7.80, status: "Empty", color: "#9ca3af" },
];

const pumps = [
  { name: "Cargo Pump 1P", status: "Running", flow: "320 m³/h", pressure: "2.8 bar" },
  { name: "Cargo Pump 2P", status: "Running", flow: "310 m³/h", pressure: "2.6 bar" },
  { name: "Cargo Pump 3P", status: "Idle",    flow: "0 m³/h",   pressure: "0.0 bar" },
  { name: "Cargo Pump 4P", status: "Running", flow: "280 m³/h", pressure: "2.4 bar" },
  { name: "Stripping Pump", status: "Running", flow: "150 m³/h", pressure: "2.2 bar" },
];

/* ── Circular gauge ── */
function GaugeCircle({ value, sublabel, trackColor = "#e2e8f0", arcColor = "#16a34a", textColor = "#0f172a", size = 110 }) {
  const r = 40, cx = size / 2, cy = size / 2;
  const circ = 2 * Math.PI * r;
  const dash = (value / 100) * circ;
  return (
    <div style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:4 }}>
      <div style={{ position:"relative", width:size, height:size }}>
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
          <circle cx={cx} cy={cy} r={r} fill="none" stroke={trackColor} strokeWidth="8"/>
          <circle cx={cx} cy={cy} r={r} fill="none" stroke={arcColor} strokeWidth="8"
            strokeDasharray={`${dash} ${circ-dash}`}
            strokeDashoffset={circ/4} strokeLinecap="round"
            transform={`rotate(-90 ${cx} ${cy})`}/>
        </svg>
        <div style={{ position:"absolute", top:"50%", left:"50%", transform:"translate(-50%,-50%)", textAlign:"center" }}>
          <div style={{ fontSize:20, fontWeight:700, color:textColor, lineHeight:1 }}>{value}%</div>
          {sublabel && <div style={{ fontSize:8, color:"#94a3b8", marginTop:2, whiteSpace:"nowrap" }}>{sublabel}</div>}
        </div>
      </div>
      <div style={{ fontSize:9, color:"#94a3b8", textTransform:"uppercase", letterSpacing:1 }}>LEVEL</div>
    </div>
  );
}

function MetricGauge({ value, label, unit, status, maxVal }) {
  const pct = Math.min((parseFloat(value) / maxVal) * 100, 100);
  const r = 38, size = 104, cx = size/2, cy = size/2;
  const circ = 2 * Math.PI * r;
  const dash = (pct / 100) * circ;
  return (
    <div style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:4 }}>
      <div style={{ position:"relative", width:size, height:size }}>
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
          <circle cx={cx} cy={cy} r={r} fill="none" stroke="#e2e8f0" strokeWidth="7"/>
          <circle cx={cx} cy={cy} r={r} fill="none" stroke="#0ea5e9" strokeWidth="7"
            strokeDasharray={`${dash} ${circ-dash}`}
            strokeDashoffset={circ/4} strokeLinecap="round"
            transform={`rotate(-90 ${cx} ${cy})`}/>
        </svg>
        <div style={{ position:"absolute", top:"50%", left:"50%", transform:"translate(-50%,-50%)", textAlign:"center" }}>
          <div style={{ fontSize:14, fontWeight:700, color:"#0f172a", lineHeight:1 }}>{value}</div>
          <div style={{ fontSize:8, color:"#94a3b8" }}>{unit}</div>
        </div>
      </div>
      <div style={{ fontSize:9, color:"#64748b", textTransform:"uppercase", letterSpacing:1 }}>{label}</div>
      {status && <div style={{ fontSize:9, color:"#16a34a", background:"#dcfce7", border:"1px solid #bbf7d0", padding:"1px 7px", borderRadius:9999 }}>{status}</div>}
    </div>
  );
}

function BarChart({ data, dataKey, maxY, unit, height=110 }) {
  const max = maxY || Math.max(...data.map(d => d[dataKey]||0)) * 1.2 || 1;
  return (
    <div style={{ display:"flex", alignItems:"flex-end", gap:5, height, paddingTop:22 }}>
      {data.map((d,i) => {
        const h2 = Math.max(2, ((d[dataKey]||0) / max) * (height-28));
        return (
          <div key={i} style={{ display:"flex", flexDirection:"column", alignItems:"center", flex:1, gap:3 }}>
            <div style={{ fontSize:8, color:"#64748b", fontWeight:600 }}>{d[dataKey]}{unit}</div>
            <div style={{ width:"100%", height:h2, background: tanks[i]?.color || "#2563eb", borderRadius:"3px 3px 0 0", opacity:0.82 }}/>
            <div style={{ fontSize:9, color:"#94a3b8" }}>{d.id}</div>
          </div>
        );
      })}
    </div>
  );
}

function TankTrend() {
  const pts = [91,90.8,91.1,91.0,90.9,91.2,91.1,91.3,91.0,90.8,91.1,91.2,91.4,91.3];
  const W=460, H=90;
  const xs = pts.map((_,i) => (i/(pts.length-1))*W);
  const ys = pts.map(v => H - ((v-88)/6)*H);
  const path = xs.map((x,i)=>`${i===0?"M":"L"}${x},${ys[i]}`).join(" ");
  const labels = ["00:00","04:00","08:00","12:00","16:00","20:00","24:00"];
  return (
    <svg width="100%" viewBox={`0 0 ${W} ${H+18}`} style={{ overflow:"visible" }}>
      {[25,50,75,100].map(v=>(
        <line key={v} x1={0} x2={W} y1={H-((v-88)/6)*H} y2={H-((v-88)/6)*H} stroke="#f1f5f9" strokeWidth="1"/>
      ))}
      <path d={path} fill="none" stroke="#2563eb" strokeWidth="2"/>
      {xs.map((x,i)=><circle key={i} cx={x} cy={ys[i]} r="3" fill="#2563eb"/>)}
      {labels.map((l,i)=>(
        <text key={i} x={(i/(labels.length-1))*W} y={H+15} textAnchor="middle" fontSize="9" fill="#94a3b8">{l}</text>
      ))}
    </svg>
  );
}
function AxisBarChart({ data, dataKey, unit, yLabel, yTicks, maxY, minY = 0 }) {
  const W = 280, H = 200, padL = 38, padB = 24, padT = 24, padR = 8;
  const chartW = W - padL - padR;
  const chartH = H - padT - padB;
  const range = maxY - minY;

  const toY = val => padT + chartH - ((val - minY) / range) * chartH;
  const barW = Math.floor(chartW / data.length) - 8;

  return (
    <svg
      width="100%"
      viewBox={`0 0 ${W} ${H + 4}`}
      style={{ overflow: "visible", display: "block", flex: 1 }}
      preserveAspectRatio="xMidYMid meet"
    >
      {/* Y-axis label */}
      <text
        x={9} y={H / 2}
        textAnchor="middle"
        fontSize="7"
        fill="#94a3b8"
        transform={`rotate(-90, 9, ${H / 2})`}
      >{yLabel}</text>

      {/* Grid lines + Y ticks */}
      {yTicks.map(v => {
        const y = toY(v);
        return (
          <g key={v}>
            <line x1={padL} x2={W - padR} y1={y} y2={y} stroke="#f1f5f9" strokeWidth="1" />
            <text x={padL - 4} y={y + 3} textAnchor="end" fontSize="8" fill="#94a3b8">{v}</text>
          </g>
        );
      })}

      {/* Bars */}
      {data.map((d, i) => {
        const val = d[dataKey] || 0;
        const barH = Math.max(2, ((val - minY) / range) * chartH);
        const x = padL + i * (chartW / data.length) + (chartW / data.length - barW) / 2;
        const y = toY(val);
        return (
          <g key={d.id}>
            {/* Value label on top */}
            <text
              x={x + barW / 2} y={y - 4}
              textAnchor="middle" fontSize="8" fontWeight="700"
              fill={tanks[i]?.color || "#2563eb"}
            >
              {val}{unit}
            </text>
            {/* Bar */}
            <rect
              x={x} y={y}
              width={barW} height={barH}
              fill={tanks[i]?.color || "#2563eb"}
              rx="3"
              opacity="0.85"
            />
            {/* X label */}
            <text
              x={x + barW / 2} y={H - padB + 14}
              textAnchor="middle" fontSize="9" fill="#94a3b8"
            >{d.id}</text>
          </g>
        );
      })}

      {/* X axis baseline */}
      <line x1={padL} x2={W - padR} y1={toY(minY)} y2={toY(minY)} stroke="#e2e8f0" strokeWidth="1" />
    </svg>
  );
}


/* ── Main component ── */
export default function Monitor2() {
  const levelColor = l => l>85?"#16a34a":l>60?"#005ee1":l>30?"#d97706":"#dc2626";

  const card = { background:"#fff", borderRadius:10, padding:16, boxShadow:"0 1px 3px rgba(0,0,0,.05)" };
  const cardTitle = { fontSize:10, fontWeight:700, color:"#005ee1", textTransform:"uppercase", letterSpacing:1.5, marginBottom:12 };
  const th = { fontSize:9, color:"#94a3b8", textTransform:"uppercase", letterSpacing:.8, padding:"5px 8px", textAlign:"left", background:"#f8fafc", borderBottom:"1px solid #e2e8f0" };
  const td = { padding:"7px 8px", fontSize:11.5, borderBottom:"1px solid #f8fafc", color:"#334155" };
  const chip = { display:"flex", alignItems:"center", gap:8, background:"#f8fafc", border:"1px solid #e2e8f0", borderRadius:8, padding:"5px 12px", fontSize:12, color:"#475569", cursor:"pointer" };
  const badge = { background:"#dc2626", color:"#fff", borderRadius:9999, fontSize:9, fontWeight:700, padding:"1px 5px", minWidth:16, textAlign:"center" };
  const pumpBadge = s => ({ display:"inline-flex", alignItems:"center", padding:"2px 8px", borderRadius:9999, fontSize:10, fontWeight:600, background: s==="Running"?"#dcfce7":"#f1f5f9", color: s==="Running"?"#16a34a":"#64748b", border: s==="Running"?"1px solid #bbf7d0":"1px solid #e2e8f0" });

  return (
     <React.Fragment>
      <div className="page-content">
         <Container fluid>
          <Card className="em-card">
<EngineHeader activePage="Cargo" activeAE="AE1" />
 <CardBody className="em-body">
    <div style={{ display:"flex", minHeight:"100vh", background:"#f1f5f9", fontFamily:"'DM Sans','Segoe UI',sans-serif", color:"#1e293b", fontSize:13 }}>

      {/* ── SIDEBAR ── */}
      

      {/* ── MAIN ── */}
      <main style={{ flex:1, display:"flex", flexDirection:"column"}}>

    


        {/* Content */}
        <div style={{ padding:"20px 24px", display:"flex", flexDirection:"column", gap:16 }}>

          {/* KPIs */}
          <div style={{ display:"grid", gridTemplateColumns:"repeat(7,1fr)", gap:12 }}>
            {[
              { label:"Total Cargo",       value:"18,450.6 t", sub:"Total on Board" },
              { label:"Cargo Capacity",    value:"25,300.0 t", sub:<span style={{color:"#16a34a",fontWeight:600}}>98.2%</span> },
              { label:"Cargo Utilization", value:"72.9%",      sub:"Utilization" },
              { label:"Voyage Progress",   value:"62%",        sub:"Completed" },
              { label:"Average Pressure",  value:"2.45 bar",   sub:"Across Tanks" },
              { label:"DWT",               value:"45,678 t",   sub:"Deadweight" },
              { label:"Discharge Port",    value:"Singapore",  sub:"Next Port" },
            ].map(k => (
              <div key={k.label} style={card}>
                <div style={{ fontSize:9, color:" rgb(0 94 225)", textTransform:"uppercase", letterSpacing:1, marginBottom:4 }}><b>{k.label}</b></div>
                <div style={{ fontSize:18, fontWeight:700, color:"#0f172a", lineHeight:1 }}>{k.value}</div>
                <div style={{ fontSize:10, color:"#94a3b8", marginTop:3 }}>{k.sub}</div>
              </div>
            ))}
          </div>

          {/* Two-column layout */}
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:16 }}>

            {/* LEFT column */}
            <div style={{ display:"flex", flexDirection:"column", gap:12 }}>

              {/* Tank overview viz */}
              <div style={card}>
                <div style={cardTitle}>Tank Overview</div>
                <div style={{ display:"flex", gap:10, marginBottom:10, fontSize:9, color:"#94a3b8" }}>
                  {[["#16a34a","> 85%"],["#2563eb","60–85%"],["#d97706","30–60%"],["#dc2626","< 30%"],["#9ca3af","Empty"]].map(([c,l])=>(
                    <div key={l} style={{ display:"flex", alignItems:"center", gap:4 }}>
                      <div style={{ width:8, height:8, borderRadius:2, background:c }}/>{l}
                    </div>
                  ))}
                </div>
                <div style={{ display:"flex", alignItems:"center", gap:6, background:"#f8fafc", border:"1px solid #e2e8f0", borderRadius:10, padding:"12px 8px" }}>
                  <div style={{ width:22, height:60, background:"#e2e8f0", borderRadius:"50% 0 0 50%", flexShrink:0 }}/>
                  {tanks.map(t=>(
                    <div key={t.id} style={{ flex:1, height:60, background: t.level===0?"#f8fafc":t.color+"18", border:`2px solid ${t.color}`, borderRadius:6, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center" }}>
                      <div style={{ fontSize:10, fontWeight:700, color:t.color }}>{t.id}</div>
                      <div style={{ fontSize:12, fontWeight:800, color: t.level===0?"#94a3b8":"#0f172a" }}>{t.level===0?"Empty":`${t.level}%`}</div>
                    </div>
                  ))}
                  <div style={{ width:18, height:60, background:"#e2e8f0", borderRadius:"0 50% 50% 0", flexShrink:0 }}/>
                </div>
              </div>

              {/* Cargo tanks table */}
              <div style={card}>
                <div style={cardTitle}>Cargo Tanks</div>
                <table style={{ width:"100%", borderCollapse:"collapse" }}>
                  <thead>
                    <tr>{["Tank","Type","Vol (t)","Cap (t)","Level","Temp","Press","Density","Status"].map(h=><th key={h} style={th}>{h}</th>)}</tr>
                  </thead>
                  <tbody>
                    {tanks.map(t=>(
                      <tr key={t.id}>
                        <td style={{ ...td, fontWeight:600 }}>{t.id}</td>
                        <td style={td}>{t.type}</td>
                        <td style={td}>{t.volume.toLocaleString()}</td>
                        <td style={td}>{t.capacity.toLocaleString()}</td>
                        <td style={{ ...td, minWidth:80 }}>
                          <div style={{ fontSize:11, fontWeight:600, marginBottom:2 }}>{t.level}%</div>
                          <div style={{ height:5, borderRadius:3, background:`linear-gradient(90deg,${levelColor(t.level)} ${t.level}%,#e2e8f0 ${t.level}%)` }}/>
                        </td>
                        <td style={td}>{t.temp}</td>
                        <td style={td}>{t.pressure}</td>
                        <td style={td}>{t.density||"—"}</td>
                        <td style={td}><span style={{ width:7, height:7, borderRadius:"50%", background: t.level===0?"#9ca3af":"#16a34a", display:"inline-block", marginRight:4 }}/>{t.status}</td>
                      </tr>
                    ))}
                    <tr style={{ background:"#f8fafc" }}>
                      <td style={{ ...td, fontWeight:700 }} colSpan={2}>Total</td>
                      <td style={td}>14,360.0</td>
                      <td style={td}>25,300.0 t</td>
                      <td style={{ ...td, color:"#2563eb", fontWeight:700 }}>72.9%</td>
                      <td style={td}>31.6 °C</td>
                      <td style={td}>2.45 bar</td>
                      <td style={td}>848.98 kg/m³</td>
                      <td style={td}/>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* Pumps + 3 charts */}
             
            </div>

          {/* RIGHT column – tank 1P details */}
<div style={{ display:"flex", flexDirection:"column", gap:12 }}>
  <div style={card}>
    <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:14 }}>
      <div style={cardTitle}>Tank Details – 1P (Port)</div>
      <div style={{ display:"flex", gap:6, alignItems:"center" }}>
        <div style={{ ...chip, fontSize:10, padding:"3px 10px" }}>Select Tank ▾</div>
        {["‹","›"].map(a=><button key={a} style={{ background:"#f8fafc", border:"1px solid #e2e8f0", borderRadius:4, width:22, height:22, color:"#64748b", cursor:"pointer", fontSize:12 }}>{a}</button>)}
      </div>
    </div>

    {/* ── 4 Gauge boxes ── */}
    <div style={{ display:"flex", gap:10, marginBottom:16 }}>

      {/* LEVEL */}
      <div style={{ flex:1, display:"flex", flexDirection:"column", alignItems:"center", border:"1px solid #e2e8f0", borderRadius:10, padding:"10px 6px", background:"#fafcff" }}>
        <div style={{ fontSize:9, color:"#94a3b8", textTransform:"uppercase", letterSpacing:1.2, marginBottom:8 }}>LEVEL</div>
        <svg width={90} height={90} viewBox="0 0 90 90">
          {(() => {
            const r=34,cx=45,cy=45,circ=2*Math.PI*r,dash=(91/100)*circ;
            return <>
              <circle cx={cx} cy={cy} r={r} fill="none" stroke="#e2e8f0" strokeWidth="7"/>
              <circle cx={cx} cy={cy} r={r} fill="none" stroke="#16a34a" strokeWidth="7"
                strokeDasharray={`${dash} ${circ-dash}`} strokeDashoffset={circ/4}
                strokeLinecap="round" transform={`rotate(-90 ${cx} ${cy})`}/>
              <text x={cx} y={cy-6} textAnchor="middle" fontSize="16" fontWeight="800" fill="#0f172a">91%</text>
              <text x={cx} y={cy+7} textAnchor="middle" fontSize="6.5" fill="#94a3b8">4,120 t</text>
              <text x={cx} y={cy+17} textAnchor="middle" fontSize="5.5" fill="#94a3b8">1.32 m ULLAGE</text>
            </>;
          })()}
        </svg>
        <div style={{ marginTop:8, fontSize:9, color:"#16a34a", background:"#dcfce7", border:"1px solid #bbf7d0", padding:"2px 12px", borderRadius:9999, fontWeight:600 }}>Normal</div>
      </div>

      {/* PRESSURE */}
      <div style={{ flex:1, display:"flex", flexDirection:"column", alignItems:"center", border:"1px solid #e2e8f0", borderRadius:10, padding:"10px 6px", background:"#fafcff" }}>
        <div style={{ fontSize:9, color:"#94a3b8", textTransform:"uppercase", letterSpacing:1.2, marginBottom:8 }}>PRESSURE</div>
        <svg width={90} height={90} viewBox="0 0 90 90">
          {(() => {
            const r=34,cx=45,cy=45,circ=2*Math.PI*r,dash=((2.70/5)*100/100)*circ;
            return <>
              <circle cx={cx} cy={cy} r={r} fill="none" stroke="#e2e8f0" strokeWidth="7"/>
              <circle cx={cx} cy={cy} r={r} fill="none" stroke="#0ea5e9" strokeWidth="7"
                strokeDasharray={`${dash} ${circ-dash}`} strokeDashoffset={circ/4}
                strokeLinecap="round" transform={`rotate(-90 ${cx} ${cy})`}/>
              <text x={cx} y={cy-2} textAnchor="middle" fontSize="18" fontWeight="800" fill="#0f172a">2.70</text>
              <text x={cx} y={cy+12} textAnchor="middle" fontSize="8" fill="#94a3b8">bar</text>
            </>;
          })()}
        </svg>
        <div style={{ marginTop:8, fontSize:9, color:"#16a34a", background:"#dcfce7", border:"1px solid #bbf7d0", padding:"2px 12px", borderRadius:9999, fontWeight:600 }}>Normal</div>
      </div>

      {/* TEMPERATURE */}
      <div style={{ flex:1, display:"flex", flexDirection:"column", alignItems:"center", border:"1px solid #e2e8f0", borderRadius:10, padding:"10px 6px", background:"#fafcff" }}>
        <div style={{ fontSize:9, color:"#94a3b8", textTransform:"uppercase", letterSpacing:1.2, marginBottom:8 }}>TEMPERATURE</div>
        <svg width={90} height={90} viewBox="0 0 90 90">
          {(() => {
            const r=34,cx=45,cy=45,circ=2*Math.PI*r,dash=((33.2/60)*100/100)*circ;
            return <>
              <circle cx={cx} cy={cy} r={r} fill="none" stroke="#e2e8f0" strokeWidth="7"/>
              <circle cx={cx} cy={cy} r={r} fill="none" stroke="#0ea5e9" strokeWidth="7"
                strokeDasharray={`${dash} ${circ-dash}`} strokeDashoffset={circ/4}
                strokeLinecap="round" transform={`rotate(-90 ${cx} ${cy})`}/>
              <text x={cx} y={cy-2} textAnchor="middle" fontSize="18" fontWeight="800" fill="#0f172a">33.2</text>
              <text x={cx} y={cy+12} textAnchor="middle" fontSize="8" fill="#94a3b8">°C</text>
            </>;
          })()}
        </svg>
        <div style={{ marginTop:8, fontSize:9, color:"#16a34a", background:"#dcfce7", border:"1px solid #bbf7d0", padding:"2px 12px", borderRadius:9999, fontWeight:600 }}>Normal</div>
      </div>

      {/* DENSITY */}
      <div style={{ flex:1, display:"flex", flexDirection:"column", alignItems:"center", border:"1px solid #e2e8f0", borderRadius:10, padding:"10px 6px", background:"#fafcff" }}>
        <div style={{ fontSize:9, color:"#94a3b8", textTransform:"uppercase", letterSpacing:1.2, marginBottom:8 }}>DENSITY</div>
        <svg width={90} height={90} viewBox="0 0 90 90">
          {(() => {
            const r=34,cx=45,cy=45,circ=2*Math.PI*r,dash=((850.2/1000)*100/100)*circ;
            return <>
              <circle cx={cx} cy={cy} r={r} fill="none" stroke="#e2e8f0" strokeWidth="7"/>
              <circle cx={cx} cy={cy} r={r} fill="none" stroke="#0ea5e9" strokeWidth="7"
                strokeDasharray={`${dash} ${circ-dash}`} strokeDashoffset={circ/4}
                strokeLinecap="round" transform={`rotate(-90 ${cx} ${cy})`}/>
              <text x={cx} y={cy-2} textAnchor="middle" fontSize="15" fontWeight="800" fill="#0f172a">850.2</text>
              <text x={cx} y={cy+12} textAnchor="middle" fontSize="7" fill="#94a3b8">kg/m³</text>
            </>;
          })()}
        </svg>
        <div style={{ marginTop:8, fontSize:9, color:"#16a34a", background:"#dcfce7", border:"1px solid #bbf7d0", padding:"2px 12px", borderRadius:9999, fontWeight:600 }}>Normal</div>
      </div>

    </div>
    {/* ── end gauges ── */}

    {/* Detail grid */}
    <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:5, fontSize:11 }}>
      {[
        ["Cargo Type","Crude Oil"],["Observed Temp.","33.2 °C"],
        ["Tank Capacity","4,500.0 t"],["Heating Set Point","33.0 °C"],
        ["Volume","4,120.0 t"],["Pressure Set Point","3.00 bar"],
        ["Weight","3,502.4 t"],["Last Gauging","03:30 UTC"],
        ["Sounding","13.68 m"],["Correction","+0.02 m"],
        ["Vapour Temp.","31.1 °C"],["Status",<span style={{color:"#16a34a",fontWeight:600}}>● Normal</span>],
      ].map(([k,v])=>(
        <div key={k} style={{ display:"flex", justifyContent:"space-between", borderBottom:"1px solid #f1f5f9", paddingBottom:5 }}>
          <span style={{ color:"#94a3b8" }}>{k}</span>
          <span style={{ color:"#334155", fontWeight:500 }}>{v}</span>
        </div>
      ))}
    </div>
  </div>

  {/* Tank Level Trend card */}
  <div style={card}>
    <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:10 }}>
      <div style={cardTitle}>Tank Level Trend – 1P (Port)</div>
      <div style={{ ...chip, padding:"2px 8px", fontSize:10 }}>24H ▾</div>
    </div>
    <div style={{ display:"flex", gap:20, marginBottom:4 }}>
      {[100,75,50,25].map(v=><span key={v} style={{ fontSize:9, color:"#94a3b8" }}>{v}</span>)}
    </div>
    <TankTrend/>
    <div style={{ display:"flex", alignItems:"center", gap:5, marginTop:6, fontSize:9, color:"#2563eb" }}>
      <div style={{ width:10, height:10, borderRadius:"50%", background:"#2563eb" }}/> Level (%)
    </div>
  </div>
</div>
        
             
          </div>
        </div>
        <Row style={{ padding: "0 24px 16px", alignItems: "stretch" }}>

  {/* Pumps & Lines */}
  <Col lg={3}>
    <div style={{ ...card, height: "100%", display: "flex", flexDirection: "column" }}>
      <div style={cardTitle}>Pumps & Lines Status</div>
      <div style={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
        {pumps.map(p => (
          <div key={p.name} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", paddingBottom: 12, borderBottom: "1px solid #f1f5f9" }}>
            <div>
              <div style={{ fontSize: 11, color: "#334155", fontWeight: 600 }}>{p.name}</div>
              <div style={{ fontSize: 9, color: "#94a3b8", marginTop: 2 }}>
                Flow: {p.flow} · Press: {p.pressure}
              </div>
            </div>
            <div style={pumpBadge(p.status)}>{p.status}</div>
          </div>
        ))}
        <div style={{ fontSize: 11, color: "#2563eb", cursor: "pointer", fontWeight: 500, paddingTop: 8 }}>
          View All Pumps & Lines →
        </div>
      </div>
    </div>
  </Col>

  {/* Cargo Temperature Overview */}
  <Col lg={3}>
    <div style={{ ...card, height: "100%", display: "flex", flexDirection: "column" }}>
      <div style={cardTitle}>Cargo Temperature Overview</div>
      <div style={{ flex: 1, display: "flex", flexDirection: "column" }}>
        <AxisBarChart
          data={tanks}
          dataKey="temp"
          unit="°C"
          yLabel="Temperature (°C)"
          yTicks={[0, 15, 30, 45, 60]}
          maxY={60}
          minY={0}
          fillHeight
        />
      </div>
    </div>
  </Col>

  {/* Pressure Overview */}
  <Col lg={3}>
    <div style={{ ...card, height: "100%", display: "flex", flexDirection: "column" }}>
      <div style={cardTitle}>Pressure Overview</div>
      <div style={{ flex: 1, display: "flex", flexDirection: "column" }}>
        <AxisBarChart
          data={tanks}
          dataKey="pressure"
          unit=" bar"
          yLabel="Pressure (bar)"
          yTicks={[0, 1, 2, 3, 4]}
          maxY={4}
          minY={0}
          fillHeight
        />
      </div>
    </div>
  </Col>

  {/* Density Overview */}
  <Col lg={3}>
    <div style={{ ...card, height: "100%", display: "flex", flexDirection: "column" }}>
      <div style={cardTitle}>Density Overview</div>
      <div style={{ flex: 1, display: "flex", flexDirection: "column" }}>
        <AxisBarChart
          data={tanks.map(t => ({ ...t, density: t.density || 0 }))}
          dataKey="density"
          unit=""
          yLabel="Density (kg/m³)"
          yTicks={[600, 750, 900, 1050, 1200]}
          maxY={1200}
          minY={600}
          fillHeight
        />
      </div>
    </div>
  </Col>
</Row>
        {/* Weather footer */}
        <div style={{ borderTop:"1px solid #e2e8f0", marginTop:"5px",background:"#fff", display:"flex", gap:24, padding:"10px 24px",justifyContent:"center"}}>
          {[
            {icon:"🌊",label:"Sea Temp.",value:"26.4 °C"},
            {icon:"🌡",label:"Ambient Temp.",value:"24.8 °C"},
            {icon:"💧",label:"Humidity",value:"78 %"},
            {icon:"⏱",label:"Barometric Press.",value:"1013 mbar"},
            {icon:"💨",label:"Wind Speed",value:"12.5 kn"},
            {icon:"🧭",label:"Wind Dir.",value:"NE"},
            {icon:"🌊",label:"Wave Height",value:"1.2 m"},
            {icon:"👁",label:"Visibility",value:"Good"},
            {icon:"☀️",label:"Weather",value:"Clear Sky"},
          ].map(w=>(
            <div key={w.label} style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:2, minWidth:60 }}>
              <div style={{ fontSize:15 }}>{w.icon}</div>
              <div style={{ fontSize:8, color:"#94a3b8", textTransform:"uppercase", letterSpacing:1 }}>{w.label}</div>
              <div style={{ fontSize:13, fontWeight:700, color:"#0f172a" }}>{w.value}</div>
            </div>
          ))}
        </div>
      

      
    </main>
    </div>
    </CardBody>
    </Card>
    </Container>
    
    </div>
    </React.Fragment>
  );
}